import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tooltip, Typography, styled } from '@mui/material';
import { history } from 'src/utils/history';
import EventService from 'src/services/EventService';
import LeadStreamsService from 'src/services/LeadStreamsService';

import { ROUTES_URL } from 'src/constants/routes';

import Plus from 'src/icons/Plus';
import {
    getStreamDisplayName,
    getStreamApprovedDeliveries,
    getStreamBalance,
    getStreamPackageSize,
    getStreamStatus,
    hasActiveStreamAccess,
    shouldOpenStreamDetails,
} from 'src/utils/leadStreams';

import type { Stream } from 'src/services/LeadStreamsService/agent';

const StreamItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.85),
    minHeight: 86,
    padding: theme.spacing(1),
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.16s ease, opacity 0.16s ease',
    '& + &': {
        marginTop: theme.spacing(1),
    },
    '&:hover': {
        backgroundColor: 'var(--lm-surface)',
    },
    '&:hover .stream-actions-line, &:focus-within .stream-actions-line': {
        opacity: 1,
        pointerEvents: 'auto',
    },
}));

const StreamTopLine = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
}));

const StreamActionsLine = styled(Box)(() => ({
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.14s ease',
    '@media (hover: none)': { opacity: 1, pointerEvents: 'auto' },
}));

const StreamList = styled(Box)(() => ({
    maxHeight: 360,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    contain: 'layout paint',
    paddingRight: 2,
    '&::-webkit-scrollbar': {
        width: 4,
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'var(--lm-border)',
        borderRadius: 999,
    },
}));

const StreamIcon = styled(Box)(() => ({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
}));

const StreamInfo = styled(Box)(() => ({
    flex: 1,
    minWidth: 0,
}));

const StreamName = styled(Typography)(() => ({
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const StreamMeta = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
}));

const StreamMetaText = styled(Typography)<{ color?: string }>(({ color }) => ({
    color: color || 'var(--lm-muted)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const StreamProgressTrack = styled(Box)(() => ({
    backgroundColor: 'var(--lm-border)',
    borderRadius: 999,
    height: 4,
    overflow: 'hidden',
}));

const StreamProgressFill = styled(Box)<{ color: string; value: number }>(({ color, value }) => ({
    background: color,
    borderRadius: 999,
    height: '100%',
    transition: 'width 0.18s ease',
    width: `${Math.max(0, Math.min(100, value))}%`,
}));

const ActionButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'subtle',
})<{ subtle?: boolean }>(({ subtle }) => ({
    alignItems: 'center',
    appearance: 'none',
    background: subtle ? 'transparent' : 'var(--lm-surface)',
    border: subtle ? '1px solid transparent' : '1px solid var(--lm-border)',
    borderRadius: 8,
    color: 'var(--lm-muted)',
    cursor: 'pointer',
    display: 'inline-flex',
    height: 28,
    justifyContent: 'center',
    padding: 0,
    transition: 'background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease',
    width: 28,
    '&:hover': {
        background: 'var(--lm-surface)',
        borderColor: 'var(--lm-border)',
        color: 'var(--lm-text)',
    },
    '& svg': {
        display: 'block',
        height: 15,
        width: 15,
    },
    '& svg path': {
        fill: 'currentColor',
    },
    '&:disabled': {
        color: 'var(--lm-disabled)',
        cursor: 'default',
        opacity: 0.58,
    },
}));

const NewStreamButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        '& .new-stream-text': {
            color: 'var(--lm-accent-ink, var(--lm-accent-ink))',
        },
        '& .plus-icon': {
            '& svg path': {
                fill: 'var(--lm-accent-ink, var(--lm-accent-ink))',
            },
        },
    },
}));

const NewStreamText = styled(Typography)(() => ({
    color: 'var(--lm-muted)',
    fontSize: 14,
    transition: 'color 0.2s ease',
}));

const PlusIcon = styled(Box)(() => ({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'var(--lm-border)',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    '& svg': {
        width: 16,
        height: 16,
        '& path': {
            fill: 'var(--lm-muted)',
            transition: 'fill 0.2s ease',
        },
    },
}));

type LeadStreamsMenuProps = {
    streams: Stream[];
};

function getStatusColor(statusKey: string) {
    if (statusKey === 'active') return 'var(--lm-success-ink)';
    if (statusKey === 'paused') return 'var(--lm-muted)';
    if (statusKey === 'ended') return 'var(--lm-danger-ink)';
    return 'var(--lm-muted)';
}

function getProgressColor(statusKey: string) {
    if (statusKey === 'ended') return 'var(--lm-danger-ink)';
    if (statusKey === 'paused') return 'var(--lm-muted)';
    if (statusKey === 'active') return 'var(--lm-accent-fill, var(--lm-accent-ink))';
    return 'var(--lm-muted)';
}

function formatMenuNumber(value: number) {
    return new Intl.NumberFormat('ru-RU').format(value);
}

function getStreamCapacityLabel(stream: Stream) {
    if (hasActiveStreamAccess(stream)) {
        if (!stream.access_expires_at) {
            return '∞';
        }

        const date = new Date(stream.access_expires_at);
        if (Number.isNaN(date.getTime())) {
            return '∞';
        }

        return `∞ до ${date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`;
    }

    const balance = getStreamBalance(stream);
    if (balance > 0) {
        return `${formatMenuNumber(balance)} осталось`;
    }

    return '';
}

function getStreamMetaLabel(stream: Stream) {
    const status = getStreamStatus(stream);
    const capacityLabel = getStreamCapacityLabel(stream);

    if (status.key === 'paused') {
        return capacityLabel ? `Пауза · ${capacityLabel}` : 'Пауза';
    }

    if (status.key === 'ended') {
        return 'Закончился';
    }

    return capacityLabel || status.label;
}

function getStreamProgressValue(stream: Stream) {
    const status = getStreamStatus(stream);
    if (status.key === 'ended') {
        return 0;
    }

    if (hasActiveStreamAccess(stream)) {
        const start = stream.access_activated_at ? new Date(stream.access_activated_at).getTime() : 0;
        const end = stream.access_expires_at ? new Date(stream.access_expires_at).getTime() : 0;
        const now = Date.now();

        if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
            return Math.max(0, Math.min(100, Math.round(((end - now) / (end - start)) * 100)));
        }

        return 100;
    }

    const packageSize = getStreamPackageSize(stream);
    const balance = getStreamBalance(stream);
    const approvedDeliveries = getStreamApprovedDeliveries(stream);
    const total = packageSize > 0 ? packageSize : balance + approvedDeliveries;

    if (total <= 0) {
        return status.key === 'pending' ? 0 : 100;
    }

    return Math.max(0, Math.min(100, Math.round((balance / total) * 100)));
}

function PauseGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 3.5C5 3.22386 5.22386 3 5.5 3H6.5C6.77614 3 7 3.22386 7 3.5V12.5C7 12.7761 6.77614 13 6.5 13H5.5C5.22386 13 5 12.7761 5 12.5V3.5Z" />
            <path d="M9 3.5C9 3.22386 9.22386 3 9.5 3H10.5C10.7761 3 11 3.22386 11 3.5V12.5C11 12.7761 10.7761 13 10.5 13H9.5C9.22386 13 9 12.7761 9 12.5V3.5Z" />
        </svg>
    );
}

function PlayGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5 3.92504C5 3.51977 5.44123 3.26777 5.79099 3.47264L12.7496 7.5476C13.095 7.74992 13.095 8.25008 12.7496 8.4524L5.79099 12.5274C5.44123 12.7322 5 12.4802 5 12.075V3.92504Z" />
        </svg>
    );
}

function TopUpGlyph() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M8.5 3.5C8.5 3.22386 8.27614 3 8 3C7.72386 3 7.5 3.22386 7.5 3.5V7.5H3.5C3.22386 7.5 3 7.72386 3 8C3 8.27614 3.22386 8.5 3.5 8.5H7.5V12.5C7.5 12.7761 7.72386 13 8 13C8.27614 13 8.5 12.7761 8.5 12.5V8.5H12.5C12.7761 8.5 13 8.27614 13 8C13 7.72386 12.7761 7.5 12.5 7.5H8.5V3.5Z" />
        </svg>
    );
}

const LeadStreamsMenu: React.FC<LeadStreamsMenuProps> = ({ streams }) => {
    const [visibleStreams, setVisibleStreams] = useState(streams);
    const [updatingStreamIds, setUpdatingStreamIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        setVisibleStreams(streams);
    }, [streams]);

    const handleNewStreamClick = useCallback(() => {
        history.push(ROUTES_URL.LEADSTREAM);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleStreamClick = useCallback((stream: Stream) => {
        history.push(shouldOpenStreamDetails(stream) ? `${ROUTES_URL.LEADSTREAM}/${stream.id}` : ROUTES_URL.LEADSTREAM);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleCheckoutClick = useCallback((event: React.MouseEvent, stream: Stream) => {
        event.stopPropagation();
        history.push(`${ROUTES_URL.LEADSTREAM}/${stream.id}?checkout=1`);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleToggleActive = useCallback(async (event: React.MouseEvent, stream: Stream) => {
        event.stopPropagation();
        setUpdatingStreamIds((prev) => new Set(prev).add(stream.id));

        try {
            const updated = await LeadStreamsService.updateStream(stream.id, { is_active: !stream.is_active });
            setVisibleStreams((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            EventService.dispatch('LEAD_STREAMS_UPDATED', updated);
        } catch (_requestError) {
            // Menu actions are intentionally quiet; the details page gives fuller feedback.
        } finally {
            setUpdatingStreamIds((prev) => {
                const next = new Set(prev);
                next.delete(stream.id);
                return next;
            });
        }
    }, []);

    return (
        <Box
            sx={{
                backgroundColor: 'var(--lm-canvas)',
                borderRadius: 2,
                p: 1,
            }}
        >
            {visibleStreams.length === 0 ? (
                <NewStreamButton onClick={handleNewStreamClick}>
                    <NewStreamText
                        variant="body1"
                        className="new-stream-text"
                    >
                        Новый поток
                    </NewStreamText>
                    <PlusIcon className="plus-icon">
                        <Plus />
                    </PlusIcon>
                </NewStreamButton>
            ) : (
                <>
                    <StreamList>
                        {visibleStreams.map((stream) => {
                            const status = getStreamStatus(stream);
                            const displayName = getStreamDisplayName(stream);
                            const metaLabel = getStreamMetaLabel(stream);
                            const progressValue = getStreamProgressValue(stream);
                            const progressColor = getProgressColor(status.key);
                            const metaColor = status.key === 'active' ? 'var(--lm-muted)' : getStatusColor(status.key);
                            const isUpdating = updatingStreamIds.has(stream.id);
                            const canToggle = status.key === 'active' || status.key === 'paused';
                            const checkoutLabel =
                                'Докупить';
                            const toggleLabel = stream.is_active ? 'Пауза' : 'Возобновить';

                            return (
                                <StreamItem
                                    key={stream.id}
                                    onClick={() => handleStreamClick(stream)}
                                >
                                    <StreamTopLine>
                                        <StreamIcon>{stream.category.icon || '📁'}</StreamIcon>
                                        <StreamInfo>
                                            <StreamName
                                                variant="body2"
                                                color="primary"
                                            >
                                                {displayName}
                                            </StreamName>
                                        </StreamInfo>
                                        <StreamActionsLine className="stream-actions-line">
                                            {canToggle && (
                                                <Tooltip title={toggleLabel}>
                                                    <span>
                                                        <ActionButton
                                                            type="button"
                                                            aria-label={toggleLabel}
                                                            title={toggleLabel}
                                                            disabled={isUpdating}
                                                            onClick={(event) => handleToggleActive(event, stream)}
                                                            subtle
                                                        >
                                                            {stream.is_active ? <PauseGlyph /> : <PlayGlyph />}
                                                        </ActionButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={checkoutLabel}>
                                                <span>
                                                    <ActionButton
                                                        type="button"
                                                        aria-label={checkoutLabel}
                                                        title={checkoutLabel}
                                                        onClick={(event) => handleCheckoutClick(event, stream)}
                                                        subtle={status.key !== 'ended'}
                                                    >
                                                        <TopUpGlyph />
                                                    </ActionButton>
                                                </span>
                                            </Tooltip>
                                        </StreamActionsLine>
                                    </StreamTopLine>
                                    <StreamMeta>
                                        <StreamMetaText color={metaColor}>{metaLabel}</StreamMetaText>
                                        <StreamProgressTrack>
                                            <StreamProgressFill
                                                color={progressColor}
                                                value={progressValue}
                                            />
                                        </StreamProgressTrack>
                                    </StreamMeta>
                                </StreamItem>
                            );
                        })}
                    </StreamList>
                    <NewStreamButton onClick={handleNewStreamClick}>
                        <NewStreamText
                            variant="body1"
                            className="new-stream-text"
                        >
                            Новый поток
                        </NewStreamText>
                        <PlusIcon className="plus-icon">
                            <Plus />
                        </PlusIcon>
                    </NewStreamButton>
                </>
            )}
        </Box>
    );
};

export default React.memo(LeadStreamsMenu);

export {
    StreamActionsLine,
    ActionButton,
    PauseGlyph,
    PlayGlyph,
    TopUpGlyph,
    StreamItem,
    StreamList,
    StreamIcon,
    StreamName,
    StreamMeta,
    StreamMetaText,
    StreamProgressTrack,
    StreamProgressFill,
    NewStreamText,
    PlusIcon,
    getStreamMetaLabel,
    getStreamProgressValue,
    getProgressColor,
};
